import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  currentIntervalSetIndex,
  intervalNoteMidi,
  intervalSetSessions,
  ladderHomeNote,
  pickHomeNote,
  pickIntervalPatterns,
} from '../../../../../common/src/interval_training.ts';
import {
  clefForVoicePart,
  currentStreak,
  intervalPatternsPerSession,
  learnLevelName,
  learnModuleName,
  markSyllablesMet,
  moduleProgress,
  needsSyllables,
  RecordedSession,
  recordLearnSession,
  voicePartForRange,
} from '../../../../../common/src/learn_rules.ts';
import { LearnLevel, LearnModule, LearnProgress, VocalRange } from '../../../../../common/src/model.ts';
import { getLearnPath, LearnPages } from '../../../../../common/src/pages.ts';
import { RandomSource } from '../../../../../common/src/rhythm_echo.ts';
import { LearnProgressUpdate, useLearnProgress } from '../../../data/use_learn_progress.tsx';
import { OuterPageContent } from '../../../util/shared.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Header } from '../../header.tsx';
import { getLearnAudioSession, LearnAudioSession, PianoNote } from '../audio/learn_audio_session.ts';
import { MicFailure, micFailureOf } from '../audio/learn_mic_capture.ts';
import { useAudioClock } from '../audio/use_audio_clock.ts';
import { learnModuleBack } from '../learn_classes.ts';
import { nowOn } from '../learn_dates.ts';
import { failureScreenIds, SingingFailureScreen } from '../learn_failure_screen.tsx';
import { learnHandedOnState, useLearnExit, useLearnFrom } from '../learn_navigation.tsx';
import { LearnSummaryScreen, summaryScreenIds } from '../learn_summary_screen.tsx';
import { intervalSoundLeadMs, playSoloNote } from './interval_audio.ts';
import { IntervalPatternScreen } from './interval_pattern_screen.tsx';
import {
  estimatorTargetMidi,
  initialIntervalSessionState,
  intervalResultColor,
  IntervalSessionAction,
  intervalSessionScore,
  IntervalSessionState,
  intervalSoundNotes,
  isSoundPlaying,
  reduceIntervalSession,
} from './interval_session.ts';
import { nearStaffMidiRange } from './interval_staff_geometry.ts';
import { IntervalStartScreen } from './interval_start_screen.tsx';
import { intervalSummaryProps } from './interval_summary.ts';
import { MeetSyllablesScreen } from './meet_syllables_screen.tsx';

// Interval Training (issues #3720, #3721): the page behind the Pitch class screen's second tile.
// Meet the syllables on a first visit, the start screen (level, the checklist of pattern sets, the
// link back to the syllables, Start), then one screen per pattern of a ten-pattern session -
// listen, sing it back, the result - and the shared summary. No footer. It is Pitch Match's page
// with patterns for notes (pitch_match_page.tsx), and keeps its shape, but for the header's arrow.
//
// The ways out. The header's back arrow goes back one screen within the page: from an open session
// (listen, sing, result, the summary or the failure screen) it closes the session and returns to
// the start screen (closeSession below), from the reopened ladder it returns to the start screen,
// and only from the start screen, or a first visit's ladder, does it leave for the Pitch class
// screen (learn_navigation.tsx). The summary's and the failure screen's "Back to Pitch" leave for
// the class screen too. A session is not a history entry, so Android's hardware back button and
// the browser's are not the header's arrow: they walk the history and leave the page from any
// screen, a session included. Leaving or closing a session mid-way records nothing.
//
// The range gate. do and every note of a session are drawn from the singer's measured range, so a
// member without a range is sent to the Range Finder, exactly as Pitch Match sends them: the
// redirect takes this entry's place and hands on where the singer came from, and that they came for
// Interval Training, so the Range Finder's result leads back here (learn_navigation.tsx).
//
// Screens. With no session open the page shows Meet the syllables while the member has not met
// them (needsSyllables) and the start screen otherwise, its checklist read from the sessions the
// member has played on each pattern set (setSessions). "Got it, let's start" records the meeting
// (markSyllablesMet, flushed at once) and the page remembers it for this visit too, so a member
// with nobody to record for is not held on the ladder. "How to say the syllables" reopens the
// ladder from the start screen; from there "Got it, let's start" and the header's arrow both
// return to the start screen and write nothing. A rung tap plays its syllable's note on the piano
// from the do the ladder is built on (ladderHomeNote, with the same limit a session's do has, so it
// is a do a session can be given). From Start on, the session's phase decides: listen, sing and
// result share the pattern screen, a failure shows the shared singing failure screen, and the
// summary the shared summary with what Interval Training adds to it.
//
// A session. Start, a tap on a set in the checklist and "Play again" open the mic first - the tap is
// the gesture - then build the session: the patterns of one set (pickIntervalPatterns) and a do that
// fits them (pickHomeNote). Start plays the current set, the first not done, or the mix of every
// set once all are done; a tapped set plays that set, done or not; Play again after Start does
// what Start would do now, with the session just recorded counted, and after a tapped set plays
// that set again. The do is chosen inside the singer's range, trimmed at the voice's ends as
// every exercise trims it, and within the notes on or near their clef's staff (nearStaffMidiRange,
// a limit that is not the voice's and so is not trimmed) wherever the range allows, and other than
// the do of the session before in this visit where there is another, so nobody learns do as one
// fixed pitch. A mic that refused opens the session on the failure screen, so Try again resumes it.
//
// Sounds. The machine asks for one sound at a time (interval_session.ts) and the page plays each
// request once: its notes - the pattern, do alone, one note, or the steps that fill a jump - as
// one run of piano notes a lead ahead of the clock (schedulePianoNotes, which stops whatever piano
// was in the air, so a new request needs no stop of its own). Once the run is
// scheduled the page reports `soundStarted` with the time the singer hears it start: the time the
// run was anchored at (later than asked after a slow soundfont load) plus the output's lag
// (heardLagMs). The machine lights the notes on the staff and ends the listening from that time,
// so on a phone whose speaker trails the clock the staff lights with the sound, and the piano's
// tail is waited out rather than scored as the singer. A run that cannot be scheduled at all is
// logged and reported as started at the clock's reading, so the session carries on without it, as
// it does when the soundfont cannot be loaded. "Sing it back" and Skip are stamped on the same
// heard clock, so a sound they cut short is waited out to what is still leaving the speaker. A
// request that goes away - "Sing it back", Skip during a sound, a failure, the summary - stops the
// piano (harmless once the sound has finished); one that ended on its own keeps its id and is left
// alone. The known-target
// estimator follows the note being sung (estimatorTargetMidi) and is unaimed when nothing is. A
// pattern whose result is green rings the lock chime once as the result is reached; never a chime
// per note, which would be in the microphone while the next note is scored.
//
// Time. Every transition comes from the frames' audio-clock times and from the audio clock polled
// on requestAnimationFrame, never from a timer. The clock is polled only while a sound is playing
// (so what is lit follows it and listen ends with it in a quiet room) or a sung note's outcome is
// waiting out its pause. A story that injects the fake session, moves its clock and emits frames
// drives the page through exactly the transitions a real microphone would.
//
// Recording. Reaching the summary records the session once, under LearnModule.IntervalTraining,
// with its green patterns for the score, exactly as Pitch Match records, and with the set it played
// (none for the mix, or for a session opened on `initialState`), so the checklist the singer
// returns to counts it; then the mic is ended and the context suspended. Leaving the page, or
// closing a session with the header's arrow, does the same without recording. The context is never
// closed.
//
// Injection. `audio` defaults to the page-wide Learn audio session and `random` to Math.random;
// stories and tests pass the fake session and a seeded source. `patternCount` shortens a session
// for a test that sings one through. `initialState` opens on a session built by hand; the real
// page always starts on Meet the syllables or the start screen, because its tap is the gesture
// that opens the mic.

export const intervalTrainingPageId = 'interval-training-page';

// "Back to Pitch" and the Pitch class screen it leads to, resolved on use, not at import
const intervalBack = () => learnModuleBack(LearnModule.IntervalTraining);

// The exercise's own prefix for the shared summary and failure screens, and the ids they render
const intervalIdPrefix = 'interval';
const summaryIds = summaryScreenIds(intervalIdPrefix);
export const intervalSummaryScreenId = summaryIds.screen;
export const intervalSummaryScoreTestId = summaryIds.score;
export const intervalSummarySquareTestId = summaryIds.square;
export const intervalPlayAgainButtonId = summaryIds.playAgain;
export const intervalBackToLearnButtonId = summaryIds.backToLearn;
const failureIds = failureScreenIds(intervalIdPrefix);
export const intervalFailureScreenId = failureIds.screen;
export const intervalRetryButtonId = failureIds.retry;
export const intervalFailureBackButtonId = failureIds.back;

// What the page shows with no session open, on its root's data-phase
export type IntervalPagePhase = 'syllables' | 'start' | IntervalSessionState['phase'];

// The levels this build can play; Medium and Hard wait for issue #3724
const selectableLevels: LearnLevel[] = [LearnLevel.Easy];

interface Props {
  audio?: LearnAudioSession;
  random?: RandomSource;
  initialLevel?: LearnLevel;
  initialState?: IntervalSessionState; // opens on the session, on this state, with its patterns as they are
  patternCount?: number; // patterns per session; a test shortens it to sing one through
}

export const IntervalTrainingPage = (props: Props) => {
  const { progress, updateProgress, flush } = useLearnProgress();
  const from = useLearnFrom();
  const range = progress.range;

  // No measured range, nowhere to put do: the Range Finder comes first
  if (!range) {
    return (
      <Navigate
        to={getLearnPath(LearnPages.Range)}
        replace
        state={learnHandedOnState(from, getLearnPath(LearnPages.IntervalTraining))}
      />
    );
  }

  return (
    <IntervalTrainingExercise
      {...props}
      range={range}
      progress={progress}
      updateProgress={updateProgress}
      flush={flush}
    />
  );
};

// What the page holds of an open session: the session state the screens read, or none until a
// tap opens one, and the sets it was opened with - the set it plays, which the record counts, and
// the set a checklist row asked for, which Play again plays again. They live and die with the
// session, so opening one sets them and closing it clears them, and nothing else has to remember
// to reset them.
interface IntervalPageState {
  session?: IntervalSessionState;
  playingSet?: number; // the set the session plays; none for the mix, or a session built by hand
  tappedSet?: number; // the set a row tap opened it on; none after Start
}

// Every action but `open` and `close` belongs to the pure reducer. `open` is the page's own,
// because a session is built outside the machine, from the level, a draw of patterns and a do
// (beginSession below), and so is `close`, which puts the page back on the start screen
// (closeSession below).
type IntervalPageAction =
  | IntervalSessionAction
  | { type: 'open'; state: IntervalSessionState; playingSet?: number; tappedSet?: number }
  | { type: 'close' };

function reduceIntervalPage(page: IntervalPageState, action: IntervalPageAction): IntervalPageState {
  if (action.type === 'open') {
    return { session: action.state, playingSet: action.playingSet, tappedSet: action.tappedSet };
  }
  if (action.type === 'close') {
    return {};
  }
  if (!page.session) {
    return page;
  }
  const session = reduceIntervalSession(page.session, action);
  // the machine hands back the state itself for an action that changes nothing (most ticks), and so
  // does the page, so React bails out of the render
  return session === page.session ? page : { ...page, session };
}

// The piano run for the sound the machine asks for: its notes from degrees to pitches on this do
function pianoNotesFor(state: IntervalSessionState): PianoNote[] {
  return intervalSoundNotes(state).map(({ degree, offsetMs, durationMs }) => ({
    midi: intervalNoteMidi(state.homeMidi, degree),
    offsetMs,
    durationMs,
  }));
}

const IntervalTrainingExercise = ({
  audio: audioProp,
  random: randomProp,
  initialLevel,
  initialState,
  patternCount = intervalPatternsPerSession,
  range,
  progress,
  updateProgress,
  flush,
}: Props & {
  range: VocalRange;
  progress: LearnProgress;
  updateProgress: (update: LearnProgressUpdate) => void;
  flush: () => Promise<void>;
}) => {
  const audio = audioProp ?? getLearnAudioSession();
  const random = randomProp ?? Math.random;
  const today = useCurrentDate();
  const [level, setLevel] = useState(initialState?.level ?? initialLevel ?? LearnLevel.Easy);
  const [recorded, setRecorded] = useState<RecordedSession | undefined>(undefined);
  // Met this visit: "Got it, let's start" moves on even when there is nobody to record it for
  const [metSyllables, setMetSyllables] = useState(false);
  const [syllablesReopened, setSyllablesReopened] = useState(false);
  const { unlocked } = moduleProgress(progress, LearnModule.IntervalTraining);
  const clef = clefForVoicePart(progress.voicePart ?? voicePartForRange(range));
  // Where a session's do may go besides the singer's range: on or near their staff (see the header)
  const nearStaff = useMemo(() => nearStaffMidiRange(clef), [clef]);
  // The do the ladder's rungs are played from: one a session of the level could be given
  const ladderHomeMidi = useMemo(() => ladderHomeNote(range, LearnLevel.Easy, nearStaff), [nearStaff, range]);

  const [page, dispatch] = useReducer(reduceIntervalPage, { session: initialState });
  const state = page.session;

  const mountedRef = useRef(true);
  const stateRef = useRef(state); // the latest committed state, which the sound effect reads
  const playedSoundIdRef = useRef<number | undefined>(undefined); // the sound request already played
  const chimedRef = useRef(false); // the result on screen has rung its chime
  const recordedRef = useRef(false);
  const previousHomeRef = useRef<number | undefined>(undefined); // the do of the session before, this visit
  // Bumped when a session closes, so whatever was under way for it is dropped when it lands: a sound
  // reported late, or a Start, Play again or Try again still waiting on the microphone
  const sessionCountRef = useRef(0);

  useEffect(() => {
    stateRef.current = state;
  });

  // Every analysed frame goes to the state machine for the page's life
  useEffect(() => audio.onFrame((frame) => dispatch({ type: 'frame', frame })), [audio]);

  // Leaving the page unaims the detector, silences anything pending, ends the mic track and
  // suspends the context, all inside the session's own leave(). A session abandoned here records
  // nothing.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      audio.leave();
    };
  }, [audio]);

  // The sound follows the state: each new request is played once and reported as heard, and a
  // request that goes away stops the piano - safe on a sound that has already finished. The ref is
  // the request already played, which keeps StrictMode's doubled effects from playing it twice.
  const soundId = state?.sound?.id;
  useEffect(() => {
    const playedId = playedSoundIdRef.current;
    if (soundId === undefined) {
      playedSoundIdRef.current = undefined;
      if (playedId !== undefined) {
        audio.stopPiano();
      }
      return;
    }
    const current = stateRef.current;
    if (playedId === soundId || !current) {
      return;
    }
    playedSoundIdRef.current = soundId;
    const requestId = soundId;
    const session = sessionCountRef.current;
    void audio
      .schedulePianoNotes(pianoNotesFor(current), audio.nowMs() + intervalSoundLeadMs)
      // When the singer hears it start, not when it was asked for: the run's real anchor (later
      // after a slow soundfont load) plus how far the speaker trails the clock. Everything the
      // machine times from the sound - the lit notes, the end of listening - is timed from that.
      .then((startedMs) => startedMs + audio.heardLagMs())
      .catch((error: unknown) => {
        // Nothing but soundStarted ends a wait on a sound: one that could not be scheduled is
        // reported as starting now, so the session carries on without it
        console.error('Interval Training: a sound could not be played', error);
        return audio.nowMs();
      })
      .then((heardMs) => {
        // Request ids start again from 1 with each session: a report for a closed one is not the next's
        if (!mountedRef.current || sessionCountRef.current !== session) {
          return;
        }
        dispatch({ type: 'soundStarted', requestId, atMs: heardMs });
      });
  }, [audio, soundId]);

  // The estimator follows the note being sung, and is unaimed when nothing is
  const targetMidi = state ? estimatorTargetMidi(state) : undefined;
  useEffect(() => {
    audio.setTargetMidi(targetMidi);
  }, [audio, targetMidi]);

  // A green pattern rings once, as its result is reached; a new attempt (listen, sing) re-arms it,
  // and a failure struck on the result and retried does not ring it again
  const attempting = state?.phase === 'listen' || state?.phase === 'sing';
  const resultGreen = state?.phase === 'result' && intervalResultColor(state) === 'green';
  useEffect(() => {
    if (attempting) {
      chimedRef.current = false;
      return;
    }
    if (!resultGreen || chimedRef.current) {
      return;
    }
    chimedRef.current = true;
    audio.scheduleChime(audio.nowMs());
  }, [audio, attempting, resultGreen]);

  // Ticks drive the sound (what is lit, and the end of listening) and the pause after a note's
  // outcome, and nothing else: while a note is simply being sung every transition comes from the
  // frames. A sound merely asked for has nothing to time yet, and the summary and the failure
  // screen never poll.
  const clockRunning =
    state !== undefined && (isSoundPlaying(state) || (state.phase === 'sing' && state.note.outcome !== undefined));
  useAudioClock(audio, clockRunning, (nowMs) => dispatch({ type: 'tick', nowMs }));

  // The summary is the one point that records the session, on the transition into it, as Pitch
  // Match records (see pitch_match_page.tsx): inside the updater, reusing its result for the
  // screen, in a layout effect so the summary's first paint already carries the flags.
  useLayoutEffect(() => {
    if (state?.phase !== 'summary' || recordedRef.current) {
      return;
    }
    recordedRef.current = true;
    const session = {
      module: LearnModule.IntervalTraining,
      level: state.level,
      score: intervalSessionScore(state.finished),
      now: nowOn(today),
      patternSet: page.playingSet,
    };
    let stored: RecordedSession | undefined;
    updateProgress((current) => {
      stored = recordLearnSession({ progress: current, ...session });
      return stored.progress;
    });
    // The recorded session is what the summary screen reads; nothing else can produce its flags.
    setRecorded(stored ?? recordLearnSession({ progress, ...session }));
    void flush();
    audio.leave(); // the session is over: the same teardown as leaving the page
  }, [audio, flush, page.playingSet, progress, state, today, updateProgress]);

  // Out of the exercise, by the header's arrow from the start screen or the summary's and failure
  // screen's button: back to the Pitch class screen
  const onBack = useLearnExit(intervalBack().path);

  // The header's arrow in a session: back to the start screen, recording nothing (a session on its
  // summary was recorded as it got there). The audio is left as leaving the page leaves it, and the
  // per-session refs are reset as beginSession resets them, so the next Start is like a first; the
  // sets go with the session (`close`). The played sound and the chime would be cleared by their
  // effects as the session goes; they are cleared here as well, so the next session does not depend
  // on those effects having run. `recorded` stays until the next session opens: it is what the
  // start screen's checklist reads, and it holds the session just counted even when there was
  // nobody to record it for.
  const closeSession = useCallback(() => {
    audio.leave();
    sessionCountRef.current += 1;
    playedSoundIdRef.current = undefined;
    chimedRef.current = false;
    recordedRef.current = false;
    dispatch({ type: 'close' });
  }, [audio]);

  // The gesture that opens the mic, reporting a refusal instead of throwing
  const openMic = useCallback(async (): Promise<MicFailure | undefined> => {
    try {
      await audio.openMic();
      return undefined;
    } catch (error) {
      return micFailureOf(error);
    }
  }, [audio]);

  const after = recorded?.progress ?? progress;
  const setSessions = intervalSetSessions(moduleProgress(after, LearnModule.IntervalTraining), level);
  // The set Start plays: the first not done, none (the mix) once every set is
  const currentSet = currentIntervalSetIndex(level, setSessions);

  // The one way into a session, behind Start, a tapped set and "Play again": the mic first, then the
  // session's patterns and do, and the failure screen if the mic refused. `tappedSet` is the set a
  // row tap asked for; without it the session plays the current set, read from the progress just
  // recorded when it comes from Play again. The sound request ids start again from 1 with each
  // session; the summary it replaces cleared the request, and so the played ref, so the first sound
  // is played as a new one. A session closed while the microphone was opening (Play again, then the
  // arrow) is not opened when it lands: the mic was ended under it.
  const beginSession = useCallback(
    async (tappedSet?: number) => {
      const sessionCount = sessionCountRef.current;
      const failure = await openMic();
      if (!mountedRef.current || sessionCountRef.current !== sessionCount) {
        return;
      }
      recordedRef.current = false;
      setRecorded(undefined);
      const playingSet = tappedSet ?? currentSet;
      const patterns = pickIntervalPatterns(level, playingSet, random, patternCount);
      const homeMidi = pickHomeNote(range, patterns, random, previousHomeRef.current, nearStaff);
      previousHomeRef.current = homeMidi;
      const opened = initialIntervalSessionState({ level, homeMidi, patterns });
      dispatch({ type: 'open', state: opened, playingSet, tappedSet });
      if (failure) {
        dispatch({ type: 'micFailed', failure });
      }
    },
    [currentSet, level, nearStaff, openMic, patternCount, random, range],
  );

  // Try again: a mic that would not open is opened again from this tap; a noisy room only resumes
  const onRetry = useCallback(async () => {
    if (state?.phase === 'failed' && state.failure !== 'tooNoisy') {
      const sessionCount = sessionCountRef.current;
      const failure = await openMic();
      if (!mountedRef.current || sessionCountRef.current !== sessionCount) {
        return; // gone, or the session was closed while the mic opened
      }
      if (failure) {
        dispatch({ type: 'micFailed', failure });
        return;
      }
    }
    dispatch({ type: 'retry' });
  }, [openMic, state]);

  // A rung of the ladder: its syllable's note from the ladder's do, never once the page has gone
  const onPlaySyllable = useCallback(
    (degree: number) => {
      const sessionCount = sessionCountRef.current;
      const stillWanted = () => mountedRef.current && sessionCountRef.current === sessionCount;
      playSoloNote(audio, intervalNoteMidi(ladderHomeMidi, degree), stillWanted).catch((error: unknown) =>
        console.error('Interval Training: a syllable could not be played', error),
      );
    },
    [audio, ladderHomeMidi],
  );

  // "Got it, let's start": recorded the first time, and only the first; reopened, it goes back
  const onSyllablesDone = useCallback(() => {
    if (syllablesReopened) {
      setSyllablesReopened(false);
      return;
    }
    updateProgress((current) => markSyllablesMet(current, nowOn(today)));
    void flush();
    setMetSyllables(true);
  }, [flush, syllablesReopened, today, updateProgress]);

  // Stamped on the clock the sounds are reported on, when the singer hears it: what the piano had
  // already rendered when the tap cut it short is still leaving the speaker for heardLagMs
  const onSingNow = useCallback(
    () => dispatch({ type: 'singNow', nowMs: audio.nowMs() + audio.heardLagMs() }),
    [audio],
  );
  const onSkip = useCallback(() => dispatch({ type: 'skip', nowMs: audio.nowMs() + audio.heardLagMs() }), [audio]);
  const onHearAgain = useCallback(() => dispatch({ type: 'hearAgain' }), []);
  const onHome = useCallback(() => dispatch({ type: 'playHome' }), []);
  const onTapSyllable = useCallback((index: number) => dispatch({ type: 'playNote', index }), []);
  const onNext = useCallback(() => dispatch({ type: 'next' }), []);
  const onSteps = useCallback(() => dispatch({ type: 'playSteps' }), []);
  const onSingAgain = useCallback(() => dispatch({ type: 'singAgain' }), []);

  const showSyllables = !state && (syllablesReopened || (needsSyllables(progress) && !metSyllables));
  const phase: IntervalPagePhase = state?.phase ?? (showSyllables ? 'syllables' : 'start');
  const sessionLevel = state?.level ?? level;
  const moduleName = learnModuleName(LearnModule.IntervalTraining);
  const title = `${moduleName} · ${learnLevelName(sessionLevel)}`;
  // The arrow closes a session or the reopened ladder, back to the start screen, and from there
  // leaves the page
  const onHeaderBack = state
    ? closeSession
    : syllablesReopened
      ? () => setSyllablesReopened(false)
      : () => void onBack();

  return (
    <OuterPageContent id={intervalTrainingPageId} data-phase={phase}>
      <Header title={title} onBack={onHeaderBack} />
      {phase === 'syllables' && <MeetSyllablesScreen onPlay={onPlaySyllable} onDone={onSyllablesDone} />}
      {phase === 'start' && (
        <IntervalStartScreen
          level={level}
          unlocked={unlocked}
          selectable={selectableLevels}
          setSessions={setSessions}
          onSelectLevel={setLevel}
          onOpenSyllables={() => setSyllablesReopened(true)}
          onStart={() => void beginSession()}
          onStartSet={(index) => void beginSession(index)}
        />
      )}
      {(state?.phase === 'listen' || state?.phase === 'sing' || state?.phase === 'result') && (
        <IntervalPatternScreen
          state={state}
          clef={clef}
          onSingNow={onSingNow}
          onHearAgain={onHearAgain}
          onSkip={onSkip}
          onHome={onHome}
          onTapSyllable={onTapSyllable}
          onNext={onNext}
          onSteps={onSteps}
          onSingAgain={onSingAgain}
        />
      )}
      {state?.phase === 'failed' && (
        <SingingFailureScreen
          failure={state.failure ?? 'unknown'}
          idPrefix={intervalIdPrefix}
          backLabel={intervalBack().label}
          onRetry={() => void onRetry()}
          onBack={() => void onBack()}
        />
      )}
      {state?.phase === 'summary' && (
        <LearnSummaryScreen
          idPrefix={intervalIdPrefix}
          moduleName={moduleName}
          level={state.level}
          {...intervalSummaryProps(state.finished)}
          module={moduleProgress(after, LearnModule.IntervalTraining)}
          dayStreak={currentStreak(after, today)}
          isNewBest={recorded?.isNewBest ?? false}
          unlockedLevel={recorded?.unlockedLevel}
          onPlayAgain={() => void beginSession(page.tappedSet)}
          backLabel={intervalBack().label}
          onBack={() => void onBack()}
        />
      )}
    </OuterPageContent>
  );
};
